import { Language } from "../core";

export type StringKey =
  | "title"
  | "subTitle"
  | "introduction"
  | "openInfoModal"
  | "introDescription"
  | "appDescription"
  | "eventDescription"
  | "closeInfoModal"
  | "waitingForMic"
  | "micError"
  | "sendingError"
  | "lessonComplete"
  | "thankYou"
  | "hearMore"
  | "recordButton"
  | "startingButton"
  | "doneButton"
  | "sendButton"
  | "discardButton"
  | "consentDescription"
  | "consentButton"
  | "doNotConsentButton"
  | "privacyPolicyButton"
  | "backToInfoButton"
  | "privacyPolicy";

type LanguageStrings = { [S in StringKey]: string };

const fiStrings: LanguageStrings = {
  title: "Robottikuoro",
  subTitle: "Opeta robotit laulamaan!",
  introduction:
    "Paina allaolevaa painiketta ja laula tai ääntele roboteille. Antamasi ääni kuullaan Robottikuoron esityksessä Helsingin juhlaviikoilla.",
  openInfoModal: "Kerro lisää",
  closeInfoModal: "Takaisin",
  introDescription:
    "Robottikuoro on ääni-installaatio, jonka ovat toteuttaneet <a href='https://ctpt.co/'>Counterpoint</a> ja <a href='https://www.tyttiarola.com/'>Tytti Arola</a>. Se koostuu kymmenistä erilaisista kaiuttimista ja laajentaa kuorosointia uusiin, ihmisäänelle mahdottomiin ulottuvuuksin.",
  appDescription:
    "Tällä sovelluksella voit opettaa Robottikuoroa laulamaan. Anna äänesi kuorolle, ja se oppii laulamaan niin kuin sinä laulat. Ääntäsi tullaan käyttämään Robottikuoron esityksissä.",
  eventDescription:
    "Robottikuoron <a href='https://helsinkifestival.fi/ohjelma/robottikuoro/'>ensiesiintyminen</a> tapahtuu Taiteiden Yönä, osana <a href='https://helsinkifestival.fi/'>Helsingin juhlaviikkoja</a>.",
  waitingForMic: "Odotetaan mikrofonia&hellip;",
  micError:
    "Emme löytäneet mikfofonia. Annathan luvan pyydettäessä. Tarvittaessa voit kokeilla toisella laitteella.",
  sendingError:
    "Pahoittelemme, äänen lähetys ei onnistunut. Yritäthän uudelleen!",
  lessonComplete: "Koulutus {} tehty.",
  thankYou: "Kiitos!",
  hearMore:
    "Tulethan kuuntelemaan Robottikuoroa Senaatintorille 15.8.<br>Kouluta halutessasi robotteja uudelleen!",
  recordButton: "Nauhoita",
  doneButton: "Pysäytä",
  startingButton: "Aloitetaan",
  sendButton: "Lähetä",
  discardButton: "Nauhoita uudelleen",
  consentDescription: "GDPR-teksti",
  consentButton: "Hyväksyn",
  doNotConsentButton: "En hyväksy",
  privacyPolicyButton: "Yksityisyydensuoja",
  backToInfoButton: "Takaisin",
  privacyPolicy: ""
};

const enStrings: LanguageStrings = {
  title: "The Robot Choir",
  subTitle: "Teach the robots to sing!",
  introduction:
    "Click the button below, then sing or babble to the Robot Choir. Your sound will be incorporated in to their performance at the European Research & Innovation Days.",
  openInfoModal: "Tell me more",
  closeInfoModal: "Back",
  introDescription:
    "The Robot Choir is a sound installation realised by <a href='https://ctpt.co/'>Counterpoint</a> and <a href='https://www.tyttiarola.com/'>Tytti Arola</a>. It consists of a large number of speakers that extend the choir sound to new dimensions that are impossible for the human voice.",
  appDescription:
    "With this app you can teach the Robot Choir to sing. Give your voice to the choir and it'll learn how to sing like you do. Your voice will then be incorporated to the Robot Choir's performance.",
  eventDescription:
    "The Robot Choir's performance will take place on 26th September at Kanal Centre Pompidou, as part of <a href='https://ec.europa.eu/info/research-and-innovation/events/upcoming-events/european-research-and-innovation-days_en'>European Research &amp; innovation days.</a>.",
  waitingForMic: "Waiting for microphone access&hellip;",
  micError:
    "Could not get microphone access. Please give us permission when prompted, or try another device.",
  sendingError:
    "Sorry, something went wrong when sending the sound. Please try again!",
  lessonComplete: "Lesson {} complete.",
  thankYou: "Thank you!",
  hearMore:
    "Hear the Robot Choir perform on 25th September at Kanal Centre Pompidou.<br>And if you'd like, teach the robots some more!",
  recordButton: "Record",
  doneButton: "Stop",
  startingButton: "Starting",
  sendButton: "Send",
  discardButton: "Record again",
  consentDescription:
    "<p>With your consent, we will store a recording of your voice and use it as part of the Robot Choir sound installation taking place on September 26.</p><p>The recording will be processed but your voice may still be identifiable from it.</p><p>We may also use the recording in future performances of the Robot Choir, operated by Counterpoint Studio OÜ and Tytti Arola.</p>",
  consentButton: "Yes, I agree  ",
  doNotConsentButton: "No, take me back",
  privacyPolicyButton: "Privacy policy",
  backToInfoButton: "Information",
  privacyPolicy: `
  <h2>Privacy Policy</h2>

  <p>This privacy policy will explain how our Counterpoint Studio OÜ uses the personal data we collect from you when you use our website.</p>
  
  <h3>Topics:</h3>
  
  <ul>
  <li>What data do we collect?</li>
  <li>How do we collect your data?</li>
  <li>How will we use your data?</li>
  <li>How do we store your data?</li>
  <li>Marketing</li>
  <li>What are your data protection rights?</li>
  <li>What are cookies?</li>
  <li>How do we use cookies?</li>
  <li>What types of cookies do we use?</li>
  <li>How to manage your cookies</li>
  <li>Privacy policies of other websites</li>
  <li>Changes to our privacy policy</li>
  <li>How to contact us</li>
  <li>How to contact the appropriate authorities</li>
  </ul>
  
  <h3>What data do we collect?</h3>
  
  <p>Counterpoint Studio OÜ collects the following data on this website:</p>
  
  <ul>
  <li>Audio samples of your voice, as you record them using our website.</li>
  </ul>

  <h3>How do we collect your data?</h3>
  
  <p>You directly provide Counterpoint Studio OÜ with the data we collect. We collect data and process data when you:</p>
  
  <ul>
  <li>Use our website and record sounds using your microphone.</li>
  </ul>
  
  <h3>How will we use your data?</h3>
  
  <p>Counterpoint Studio OÜ collects your data on this website so that we can create artistic sound installations taking place at various locations.</p>
  <p>The sound recordings you provide will be processed but your voice and identity may still be identifiable from them.</p>

  <h3>How do we store your data?</h3>
  
  <p>Counterpoint Studio OÜ securely stores your data at Amazon Web Services's data centers within the EU.</p>
  <p>Counterpoint Studio OÜ may keep your audio samples indefinitely.</p>
  
  <h3>What are your data protection rights?</h3>
  
  <p>Counterpoint Studio OÜ would like to make sure you are fully aware of all of your data protection rights. Every user is entitled to the following:</p>
  
  <ul>
  <li>The right to access – You have the right to request Counterpoint Studio OÜ for copies of your personal data. We may charge you a small fee for this service.</li>
  <li>The right to rectification – You have the right to request that Counterpoint Studio OÜ correct any information you believe is inaccurate. You also have the right to request Our Company to complete the information you believe is incomplete.</li>
  <li>The right to erasure – You have the right to request that Counterpoint Studio OÜ erase your personal data, under certain conditions.</li>
  <li>The right to restrict processing – You have the right to request that Counterpoint Studio OÜ restrict the processing of your personal data, under certain conditions.</li>
  <li>The right to object to processing – You have the right to object to Counterpoint Studio OÜ's processing of your personal data, under certain conditions.</li>
  <li>The right to data portability – You have the right to request that Counterpoint Studio OÜ transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
  </ul>

  <p>If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us at our email: info@ctpt.co.</p>
  
  <h3>Cookies</h3>
  
  <p>Cookies are text files placed on your computer to collect standard Internet log information and visitor behavior information. When you visit our websites, we may collect information from you automatically through cookies or similar technology</p>
  
  <p>For further information, visit allaboutcookies.org.</p>
  
  <h3>How do we use cookies?</h3>
  
  <p>Counterpoint Studio OÜ does not use cookies on this website.</p>
  
  <h3>How to manage cookies</h3>
  
  <p>You can set your browser not to accept cookies, and the above website tells you how to remove cookies from your browser. </p>
  
  <h3>Privacy policies of other websites</h3>
  
  <p>The website contains links to other websites. Our privacy policy applies only to our website, so if you click on a link to another website, you should read their privacy policy.</p>
  
  <h3>Changes to our privacy policy</h3>
  
  <p>Counterpoint Studio OÜ keeps its privacy policy under regular review and places any updates on this web page. This privacy policy was last updated on 17 September 2019.</p>
  
  <h3>How to contact us</h3>
  
  <p>If you have any questions about Counterpoint Studio OÜ's privacy policy, the data we hold on you, or you would like to exercise one of your data protection rights, please do not hesitate to contact us.</p>
  
  <p>Email us at: info@ctpt.co</p>
  
  <h3>How to contact the appropriate authority</h3>

  <p>Should you wish to report a complaint or if you feel that Our Company has not addressed your concern in a satisfactory manner, you may contact the Estonian Data Protection Inspectorate's Office.</p>

  <p>Email: info@aki.ee</p>`
};

export function getString(language: Language, key: StringKey) {
  let strings = language === "english" ? enStrings : fiStrings;
  return strings[key];
}

export function getDefaultLanguage(): Language {
  return "english";
  // let storedLang = window.localStorage.getItem("defaultLanguage");
  // if (storedLang) {
  //   return storedLang as Language;
  // } else {
  //   let nativeLang = navigator.language.toLocaleLowerCase();
  //   return nativeLang === "fi" || nativeLang === "fi-fi" ? "suomi" : "english";
  // }
}

export function setDefaultLanguage(lang: Language) {
  window.localStorage.setItem("defaultLanguage", lang);
}
